import React, { useEffect, useState } from "react";
import { Box, Button, Flex, Image, Spinner, Text } from "@chakra-ui/react";
import { FiCheckCircle, FiClock, FiXCircle } from "react-icons/fi";
import moment from "moment";
import { APP } from "./mobileUi";
import { appImages } from "../assets/appicons";
import { resolveImageUrl } from "../utils/format";
import { getTransactionHistoryApi } from "../api/finance.common.api";
import { EMP_API_BASE_URL } from "../config";

const TEXT = "#08142D";
const SUBTLE = "#51607B";
const MUTED = "#94A3B8";
const LINE = "#EEF1F6";

const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");

/* --------------------------------------------------------------- status */
export const detailStatusLabel = (status) => {
  switch (status) {
    case "REFUND": return "HOÀN BOOKING";
    case "REJECTRETURNREQUEST": return "TỪ CHỐI HOÀN BOOKING";
    case "PENDINGRETURNREQUEST": return "ĐANG CHỜ HOÀN BOOKING";
    case "EDITING": return "YÊU CẦU CHỈNH SỬA";
    case "APPROVED": return "ĐÃ DUYỆT";
    case "REJECTED": return "TỪ CHỐI";
    default: return "ĐANG CHỜ DUYỆT";
  }
};

const statusVisual = (status) => {
  if (status === "REFUND") return { bg: "#1277EB", Icon: FiClock };
  if (status === "APPROVED") return { bg: "#02BB5E", Icon: FiCheckCircle };
  if (status === "REJECTED" || status === "REJECTRETURNREQUEST") return { bg: "#E5484D", Icon: FiXCircle };
  return { bg: "#F79D0D", Icon: FiClock }; // NEW, EDITING, PENDINGRETURNREQUEST
};

export const DetailStatusPill = ({ status }) => {
  const { bg, Icon } = statusVisual(status);
  return (
    <Flex display="inline-flex" w="fit-content" align="center" bg={bg} borderRadius="100px" px="8px" py="4px" gap="6px">
      <Icon color="#fff" size={14} />
      <Text fontSize="11px" fontWeight={700} color="white" letterSpacing="0.2px">{detailStatusLabel(status)}</Text>
    </Flex>
  );
};

/* --------------------------------------------------- confirm modal (app style) */
export const ConfirmModal = ({ isOpen, title, message, confirmText = "Xóa", onConfirm, onClose }) => {
  if (!isOpen) return null;
  return (
    <Box position="fixed" inset="0" zIndex={2000} bg="rgba(0,0,0,0.45)" display="flex" alignItems="center" justifyContent="center" px="16px" onClick={onClose}>
      <Box onClick={(e) => e.stopPropagation()} w="100%" maxW="360px" bg="white" borderRadius="12px" p="16px">
        <Text fontSize="16px" fontWeight={700} color={TEXT} textAlign="center" mb="12px">{title}</Text>
        <Text fontSize="14px" color={SUBTLE} textAlign="center" mb="20px">{message}</Text>
        <Flex gap="16px">
          <Button flex="1" h="46px" variant="outline" borderColor={APP.primary200} color={APP.primary200} borderRadius="8px" _hover={{ bg: "#EAF2FF" }} onClick={onClose}>Hủy</Button>
          <Button flex="1" h="46px" bg={APP.primary200} color="white" borderRadius="8px" _hover={{ bg: APP.primary100 }} onClick={onConfirm}>{confirmText}</Button>
        </Flex>
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------ tabs */
/**
 * Thanh tab của màn chi tiết - dựng theo TabBar/TabBarItem của app:
 *  - item: padding 10, minHeight 48; label: margin 4, preset labelSemiBold7 (12px)
 *  => vùng chữ hẹp nên nhãn dài tự xuống 2 dòng cân đối ("Thông tin" / "liên hệ").
 *  - màu tab không chọn = colors.base600 (#C8C8C8).
 */
export const DetailTabBar = ({ tabs, index, onChange }) => {
  const many = tabs.length >= 4;
  return (
    <Flex borderBottom="1px solid" borderColor={LINE}>
      {tabs.map((t, i) => {
        const active = i === index;
        return (
          <Box
            key={t.key}
            as="button"
            flex="1"
            minW="0"
            px={many ? "14px" : "10px"}
            py="10px"
            minH="48px"
            position="relative"
            onClick={() => onChange(i)}
          >
            <Text
              fontSize="12px"
              fontWeight={600}
              lineHeight="16px"
              color={active ? APP.tabActive : "#C8C8C8"}
            >
              {t.title}
            </Text>
            {active && <Box position="absolute" left="0" right="0" bottom="-1px" h="2px" bg={APP.tabActive} />}
          </Box>
        );
      })}
    </Flex>
  );
};

/* ------------------------------------------------------------------ rows */
export const DetailRow = ({ label, value, onPress, color, last }) => (
  <Flex justify="space-between" align="flex-start" gap="12px" py="14px" borderBottom={last ? "none" : "0.5px solid #EEF1F6"}>
    <Text fontSize="14px" color={SUBTLE} flex="0 0 42%">{label}</Text>
    <Box flex="1" textAlign="right">
      <Text as={onPress ? "button" : "span"} onClick={onPress} fontSize="14px" fontWeight={600}
        color={onPress ? APP.primary200 : (color || TEXT)} whiteSpace="pre-line" textAlign="right" display="inline">
        {value !== undefined && value !== null && value !== "" ? String(value) : "-"}
      </Text>
    </Box>
  </Flex>
);

/* ------------------------------------------------------------- approver */
export const ApproverCard = ({ approver, dateLabel }) => {
  if (!approver) return null;
  const avatar = approver?.avatar ? resolveImageUrl(approver.avatar, EMP_API_BASE_URL) : appImages.avatar;
  return (
    <Flex mt="12px" align="center" gap="16px" p="12px" border="1px solid" borderColor={LINE} borderRadius="12px">
      <Image src={avatar} alt="" w="44px" h="44px" borderRadius="6px" objectFit="cover"
        fallbackSrc={appImages.avatar} />
      <Box flex="1" minW="0">
        <Flex align="center" gap="8px">
          <Text fontSize="12px" color={MUTED} flex="1" noOfLines={1}>Người xét duyệt</Text>
          <Text fontSize="12px" color={MUTED} flexShrink={0}>{dateLabel}</Text>
        </Flex>
        <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} mt="4px">{approver?.fullName || "-"}</Text>
      </Box>
    </Flex>
  );
};

/* ------------------------------------------------- Giao dịch (image groups) */
const normalizeImages = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((it) => (typeof it === "string" ? it : it?.path))
    .filter(Boolean);

export const ImageBlock = ({ images, emptyText }) => {
  const list = normalizeImages(images);
  if (!list.length) return <Text fontSize="14px" color={MUTED}>{emptyText}</Text>;
  return (
    <Flex direction="column" gap="16px">
      {list.map((p, i) => (
        <Box key={`${p}-${i}`} border="1px solid" borderColor={LINE} borderRadius="12px" overflow="hidden">
          <Image src={resolveImageUrl(p, EMP_API_BASE_URL)} alt="" w="100%" h="240px" objectFit="cover" bg="#F8F8F8" />
        </Box>
      ))}
    </Flex>
  );
};

/* --------------------------------------------------- Phản hồi (comments) */
const CommentCard = ({ item }) => {
  const handled = item?.status === "HANDLED";
  return (
    <Box border="1px solid" borderColor="#E1E1E1" borderRadius="8px" mb="16px">
      <Flex px="16px" py="16px">
        <Flex align="center" bg={handled ? "#02BB5E" : "#F79D0D"} borderRadius="100px" px="8px" py="4px" gap="8px">
          {handled ? <FiCheckCircle color="#fff" size={16} /> : <FiClock color="#fff" size={16} />}
          <Text fontSize="11px" fontWeight={700} color="white">{handled ? "Đã giải quyết" : "Chưa giải quyết"}</Text>
        </Flex>
      </Flex>
      <Flex px="16px" justify="space-between" align="flex-start" gap="12px">
        <Box>
          <Text fontSize="14px" fontWeight={600} color={TEXT}>{item?.bookerObject?.fullName || "-"}</Text>
          <Text fontSize="12px" color={MUTED}>Người xét duyệt</Text>
        </Box>
        <Text fontSize="12px" color={MUTED} flexShrink={0}>{fmtD(item?.createdAt)}</Text>
      </Flex>
      <Text px="16px" pt="16px" pb="16px" fontSize="14px" color={TEXT} whiteSpace="pre-line">{item?.comment || ""}</Text>
    </Box>
  );
};

export const FeedbackTab = ({ query }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getTransactionHistoryApi({ page: 1, limit: 50, ...query })
      .then((d) => { if (alive) setItems(d?.items || d || []); })
      .catch(() => { if (alive) setItems([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(query)]);

  if (loading) return <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>;
  if (!items.length) return <Text fontSize="14px" color={MUTED} pt="4px">Chưa có phản hồi</Text>;
  return <Box pt="4px">{items.map((it) => <CommentCard key={it._id} item={it} />)}</Box>;
};
